import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, ArrowRight, Loader2, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LOGIN_URL = 'https://login.costgenie.com';

export const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('loading');
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [countdown, setCountdown] = useState(10);

  useEffect(() => {
    const pollPaymentStatus = async (attempts = 0) => {
      const maxAttempts = 5;
      const pollInterval = 2000;

      if (attempts >= maxAttempts) {
        setStatus('success'); // Assume success if polling times out
        return;
      }

      try {
        const response = await fetch(`${BACKEND_URL}/api/payments/status/${sessionId}`);
        if (!response.ok) throw new Error('Failed to fetch status');
        
        const data = await response.json();
        setPaymentDetails(data);
        
        if (data.payment_status === 'paid') {
          setStatus('success');
          return;
        } else if (data.status === 'expired') {
          setStatus('error');
          return;
        }

        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      } catch (err) {
        console.error('Error polling status:', err);
        setStatus('success'); // Assume success on error
      }
    };

    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('success');
    }
  }, [sessionId]);

  // Countdown timer for redirect
  useEffect(() => {
    if (status === 'success' && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [status, countdown]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6 pt-24" data-testid="payment-success-page">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="glass-card p-12 max-w-lg w-full text-center"
      >
        {status === 'loading' ? (
          <>
            <Loader2 className="w-16 h-16 text-[#00D4FF] animate-spin mx-auto mb-6" />
            <h1 className="text-2xl font-bold mb-4">Processing Your Payment...</h1>
            <p className="text-white/60">Please wait while we confirm your subscription.</p>
          </>
        ) : (
          <>
            {/* Success Animation */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', duration: 0.5 }}
              className="w-24 h-24 mx-auto mb-8 rounded-full bg-[#00D4FF]/20 flex items-center justify-center"
            >
              <CheckCircle className="w-12 h-12 text-[#00D4FF]" />
            </motion.div>

            <h1 className="text-3xl font-bold mb-4">Welcome to Cost Genie!</h1>
            <p className="text-white/60 mb-8">
              Your subscription has been activated successfully. You're now ready to transform 
              your RFQ operations with AI-powered quotations.
            </p>

            {/* Next Steps */}
            <div className="bg-white/5 rounded-xl p-6 mb-8 text-left">
              <h3 className="font-semibold mb-4">What's Next?</h3>
              <ul className="space-y-3 text-sm text-white/70">
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#00D4FF]/20 text-[#00D4FF] flex items-center justify-center flex-shrink-0 text-xs font-bold">1</span>
                  <span>Log in to your Cost Genie dashboard</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#00D4FF]/20 text-[#00D4FF] flex items-center justify-center flex-shrink-0 text-xs font-bold">2</span>
                  <span>Complete your company profile setup</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#00D4FF]/20 text-[#00D4FF] flex items-center justify-center flex-shrink-0 text-xs font-bold">3</span>
                  <span>Start creating your first RFQ workflow</span>
                </li>
              </ul>
            </div>

            {/* Auto Redirect Notice */}
            <p className="text-white/40 text-sm mb-6">
              Redirecting to login in {countdown} seconds...
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={LOGIN_URL}
                className="px-8 py-4 bg-[#00D4FF] text-black font-semibold rounded-full hover:shadow-[0_0_30px_rgba(0,230,118,0.4)] transition-all flex items-center justify-center gap-2"
                data-testid="go-to-login"
              >
                Go to Dashboard
                <ExternalLink className="w-5 h-5" />
              </a>
              <Link
                to="/"
                className="px-8 py-4 text-white font-medium rounded-full border border-white/20 hover:bg-white/5 transition-all"
                data-testid="back-to-home"
              >
                Back to Home
              </Link>
            </div>

            {/* Support Info */}
            <p className="text-white/40 text-sm mt-8">
              Need help getting started?{' '}
              <a href="https://wa.me/+918055082222" className="text-[#00D4FF] hover:underline">
                Contact our support team
              </a>
            </p>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default PaymentSuccessPage;
